import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useBlockCardStore } from '@lib/store';
import { useAccountContext } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import { ReasonEnum } from '@shared/api';
import { MobileHeader, Success } from '@widgets';
import { Button, RadioInput, Spinner, TextInput } from '@shared/widgets';

interface IBlockCardForm {
  reason?: ReasonEnum;
  comment: string;
  password: string;
}
const DEFAULT_VALUES: IBlockCardForm = { reason: undefined, comment: '', password: '' };

export const BlockCard = () => {
  const blockCardStore = useBlockCardStore();

  const { card } = useAccountContext();
  const blockCardForm = useForm<IBlockCardForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('card');

  useEffect(() => {
    return () => blockCardStore.clear();
  }, []);

  const onSubmit = (data: IBlockCardForm) => {
    if (!card) return;
    blockCardStore.block({ cardId: card.id, reason: data.reason!, comment: data.comment, password: data.password });
  };

  const reasonOptions = useMemo(
    () => [
      { label: t('lostOrStolenCSard.reasons.lost'), value: ReasonEnum.Lost },
      { label: t('lostOrStolenCSard.reasons.stolen'), value: ReasonEnum.Stolen },
      { label: t('lostOrStolenCSard.reasons.temporary'), value: ReasonEnum.Temporary },
    ],
    [t],
  );
  if (!card?.id) return null;
  return (
    <>
      <MobileHeader />
      <form
        className='p-10 text-base font-medium relative min-h-[400px] flex flex-col flex-nowrap justify-center align-center'
        noValidate
        autoComplete='off'
        onSubmit={blockCardForm.handleSubmit(onSubmit)}
      >
        {blockCardStore.loading && <Spinner position dark />}
        {blockCardStore.success && <Success />}
        <p className='md:text-[20px] text-[16px] leading-[1.2] text-center'>{t('lostOrStolenCSard.description')}</p>
        <Controller
          name='reason'
          control={blockCardForm.control}
          rules={{ required: t('errors.form.requiredField', { ns: 'common', filed: t('lostOrStolenCSard.placeholders.reason') }) }}
          render={({ field, fieldState: { error } }) => (
            <RadioInput
              {...field}
              caption={t('lostOrStolenCSard.placeholders.selectBlockingReason')}
              field='reason'
              values={reasonOptions}
              required
              error={error?.message}
            />
          )}
        />
        <Controller
          name='comment'
          control={blockCardForm.control}
          render={({ field }) => (
            <div className='w-[248px]'>
              <div className='text-[#4c4c52] text-[18px] font-[600] tracking-[-0.1px] leading-[25px] mb-[8px] none'>
                {t('lostOrStolenCSard.placeholders.leaveComment')}
              </div>
              <textarea
                {...field}
                className={clsx(
                  'bg-[#fcfcfc] border-[#9f9fa5] text-[#4c4c52] text-[18px] font-[600] h-[100px] tracking-[0.3px] w-full resize-none outline-none shadow-[inset_0_2px_6px_0_rgba(85,85,90,0.39)]',
                  PaletteUtils.isBordierPalette && 'focus:!border-[#dc291c]',
                )}
              />
            </div>
          )}
        />
        <Controller
          name='password'
          control={blockCardForm.control}
          rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.password', { ns: 'common' }) }) }}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              caption={t('placeholders.password', { ns: 'common' })}
              name='password'
              type='password'
              required
              error={error?.message || blockCardStore.error === t('errors.form.password.wrong')}
            />
          )}
        />
        {blockCardStore.error && <p className={'text-[#d44112] -mt-[20px]'}>{blockCardStore.error}</p>}
        <Button className='mt-10 -tracking-[0.2px] text-[#4c4c52]' fullWidth caption={t('lostOrStolenCSard.buttons.blockCard')} type='submit' />
      </form>
    </>
  );
};
